import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, ChevronDown } from 'lucide-react';
import { LogoIcon } from './Logo';

const Footer = () => {
  const [openSection, setOpenSection] = useState(null);
  const settings = null;

  const whatsappNumber = settings?.whatsappNumber || '918722447768';
  const tagline = settings?.footerTagline || 'Lab grown diamonds, IGI certified. BIS Hallmarked Jewellery. Crafted with intention in India.';
  const copyright = settings?.footerCopyright || `© ${new Date().getFullYear()} House of Seya. All rights reserved.`;

  const waLink = (text) => `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`;

  const footerSections = [
    {
      title: 'Shop',
      links: [
        { label: 'Earrings', to: '/collections/earrings' },
        { label: 'Bracelets', to: '/collections/bracelets' },
        { label: 'Necklaces', to: '/collections/necklaces' },
        { label: 'New Arrivals', to: '/collections/earrings' },
        { label: 'Gifting', to: '/services/gifting' }
      ]
    },
    {
      title: 'About',
      links: [
        { label: 'Our Story', to: '/about' },
        { label: 'Craftsmanship', to: '/pages/diamond-education' },
        { label: 'Sustainability', to: '/pages/diamond-education' },
        { label: 'The Edit', to: '/blogs' },
        { label: 'Press', to: '/about' }
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Contact Us', href: waLink("Hi House of Seya! I'd like some help.") },
        { label: 'Shipping', to: '/faqs' },
        { label: 'Returns & Exchange', to: '/faqs' },
        { label: 'Ring Sizing', href: waLink('Hi House of Seya! I need help with ring sizing.') },
        { label: 'Care Guide', to: '/pages/gold-vermeil' },
        { label: 'FAQ', to: '/faqs' }
      ]
    },
    {
      title: 'Policies',
      links: [
        { label: 'Privacy Policy', href: waLink('Hi House of Seya! Could you share our Privacy Policy?') },
        { label: 'Terms of Service', href: waLink('Hi House of Seya! Could you share our Terms of Service?') },
        { label: 'Buyback Policy', to: '/faqs' },
        { label: 'Refund Policy', to: '/faqs' }
      ]
    }
  ];

  const toggleSection = (title) => {
    setOpenSection((prev) => (prev === title ? null : title));
  };

  return (
    <footer className="bg-[var(--brand-green)] text-[var(--brand-green-foreground)] border-t border-[var(--brand-green-border)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Top Brand & Header Area */}
        <div className="pt-14 pb-8 lg:pt-16 lg:pb-12 text-center border-b border-[var(--brand-green-border)]">
          <Link
            to="/"
            className="inline-flex flex-col items-center leading-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand-green-accent)] rounded-sm"
            aria-label="House of Seya Home"
          >
            <LogoIcon size={44} className="mb-2" />
            <div className="font-script text-4xl sm:text-5xl text-[var(--brand-cream)] font-normal tracking-wide">
              House of Seya
            </div>
          </Link>

          <p className="text-xs sm:text-sm text-[var(--brand-green-muted)] max-w-md mx-auto mt-4 leading-relaxed font-sans">
            {tagline}
          </p>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-5 mt-6">
            <a
              href={settings?.instagramUrl || 'https://instagram.com/houseofseya'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="House of Seya Instagram"
              className="text-[var(--brand-green-muted)] hover:text-[var(--brand-green-accent)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand-green-accent)] rounded-sm"
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>

            <a
              href={settings?.facebookUrl || 'https://facebook.com/houseofseya'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="House of Seya Facebook"
              className="text-[var(--brand-green-muted)] hover:text-[var(--brand-green-accent)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand-green-accent)] rounded-sm"
            >
              <Facebook size={18} strokeWidth={1.5} />
            </a>

            <a
              href={settings?.youtubeUrl || 'https://youtube.com/@houseofseya'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="House of Seya YouTube"
              className="text-[var(--brand-green-muted)] hover:text-[var(--brand-green-accent)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand-green-accent)] rounded-sm"
            >
              <Youtube size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* Main Footer Navigation */}
        <div className="py-8 lg:py-14 grid grid-cols-1 lg:grid-cols-4 gap-0 lg:gap-10">
          {footerSections.map((section) => (
            <div key={section.title} className="border-b border-[var(--brand-green-border)] lg:border-b-0">
              <button
                type="button"
                onClick={() => toggleSection(section.title)}
                className="w-full flex items-center justify-between py-4 text-left lg:hidden focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand-green-accent)] rounded-sm"
              >
                <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-accent)] font-medium">
                  {section.title}
                </span>
                <ChevronDown
                  size={16}
                  strokeWidth={1.5}
                  className={`text-[var(--brand-green-accent)] transition-transform duration-300 ${
                    openSection === section.title ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div className="hidden lg:block text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-accent)] font-medium mb-5">
                {section.title}
              </div>

              <div
                className={`overflow-hidden transition-all duration-300 lg:!max-h-none lg:!opacity-100 ${
                  openSection === section.title ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
                }`}
              >
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      {link.href ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-xs sm:text-sm text-[var(--brand-green-muted)] hover:text-[#FFFFFF] transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={link.to}
                          className="inline-block text-xs sm:text-sm text-[var(--brand-green-muted)] hover:text-[#FFFFFF] transition-colors"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Bar / Copyright */}
        <div className="py-7 border-t border-[var(--brand-green-border)] flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-[var(--brand-green-muted)] text-center sm:text-left">
          <div>{copyright}</div>
          <div className="tracking-widest-2 uppercase">Made in India · IGI Certified</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;